import React, { useLayoutEffect, useRef, useState } from 'react';
import Card from '../Card';
import Button from '../Button';
import DetailRow from '../DetailRow';
import { useLocale, useMoneyFormat, useDatePattern } from '../../context/LocaleContext';
import { coreSync } from '../../lib/core';
import { initials } from '../../lib/avatar';
import StatusBadge from './StatusBadge';
import EditPencil from './edit/EditPencil';
import { formatCredits } from '../../domain/makeup';
import { packageTimeLines } from '../../domain/packageSale';
import { figureOrDash } from '../../domain/reading';
import s from './StudentSidebar.module.css';

// The `Package Time` block (#181).
//
// MMS shows two figures and a badge, with `Add Package` immediately under
// them:
//
//     Package Time  (?)
//     Upcoming Lessons: 4h 30m
//     Available: 0h 30m
//     [ Low Hours ]
//
// The pair is the idea: "booked against left" says the series outruns the
// package without arithmetic, and the remedy is the next control down.
//
// The figures are minutes, rendered `4h 30m` by `packageTemplates.formatClock`
// as MMS renders them: packages are time because a make-up credit is time
// (#352), and two units in one ledger would not add up (see the head of
// `src/domain/packageTemplates.js`).
//
// No `Low Hours` badge: MMS's threshold is unknown — it was seen unlit at
// `Available 2h 30m` against `Upcoming 5h 00m` — and the two figures say the
// same thing. No `Purchased` or `Used` either: MMS gives two figures about
// what happens next, and the Package Activity log carries a running balance
// against the dates.
//
// `Unmarked past lessons` (#347) has no MMS counterpart. That time will draw
// on the package when somebody marks those lessons, so `Available` is
// provisional by that much; it renders only when there is some.
//
// The lines and their words are `packageTimeLines` in src/domain/packageSale.js,
// shared with the phones. The next package to lapse is the first in draw order
// still holding time, the decision in `core/src/packages.rs`. A caller that has
// not wired the forecast still gets the balance, and no `Upcoming Lessons:
// 0h 00m` out of an absent answer.
function PackageTime({ summary, forecast }) {
  // The next expiry's date is drawn in the studio's pattern by the core (#510).
  const datePattern = useDatePattern();
  const lines = packageTimeLines(summary, forecast, datePattern);
  if (summary.purchased === 0 || !forecast) {
    const [l] = lines;
    return <>{l.lead}{l.value ? <b>{l.value}</b> : null}{l.tail}</>;
  }
  return (
    <>
      {lines.map((l) => (
        <div key={l.lead} className={l.note ? s.blockNote : undefined}>
          {l.lead}<b>{l.value}</b>{l.tail}
        </div>
      ))}
    </>
  );
}

// The Notes card's body: five lines, then `Show More` (#136), as MMS's student
// profile Notes card clamps a long note to roughly five lines with a `Show
// More` link beneath it. `STUDENT_NOTE_MAX` (domain/people.js) is far above
// anything typed, so a long but ordinary note would otherwise push every card
// below this one off the screen.
//
// Whether anything is hidden depends on the rendered box, not the string — how
// many lines a note takes depends on the rail's width — so the element is
// measured: `scrollHeight > clientHeight` while clamped, +1 for the sub-pixel
// rounding a fractional line-height leaves. Measured only while collapsed,
// because expanding makes the two heights equal and re-measuring would
// withdraw the only way back; once true it stays true.
//
// No ResizeObserver: jsdom has none, and it would put an untestable branch in
// front of this card's only behaviour. Narrowing the window can push a
// four-line note to six without re-measuring, and it then renders clamped with
// no link.
//
// `Show Less` is ours: MMS's collapse wording is unrecorded, and a one-way
// expansion would be a worse card.
//
// `.noteBody` sets `white-space: pre-wrap` (#150), so a note typed as eight
// short lines renders as eight lines, and reaches `Show More` accordingly.
// Whether MMS renders this card's newlines is unchecked; its field is a plain
// <textarea>, so newlines are the only structure a teacher can type there.
// The students list's Notes column is #136's other half (`cellText` in
// columns.js).
function NoteBody({ note }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const bodyRef = useRef(null);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = bodyRef.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [note, expanded]);

  if (!note) {
    return <div className={s.noteEmpty}>No private note for this student yet.</div>;
  }

  return (
    <>
      <div
        ref={bodyRef}
        className={expanded ? s.noteBody : `${s.noteBody} ${s.noteClamped}`}
      >
        {note}
      </div>
      {overflowing ? (
        <button className={s.noteToggle} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show Less' : 'Show More'}
        </button>
      ) : null}
    </>
  );
}

// The left rail: who this student is, and the standing facts about their
// lessons. Presentational apart from the three buttons it reports upward — the
// two package ones and `Set Make-Up Credits` (#178).
//
// Reporting `Set Make-Up Credits` upward keeps this rail loadable in the `ui`
// jest project: the drawer writes through `data/useMakeup.jsx`, which reaches
// `src/lib/firebase.js`, and that project has no mapper for it, so a ui test
// cannot reach Firestore.
//
// `reads.makeup` and `reads.packages` (#629): the credit and package figures
// are the dash until their ledgers have answered — a zero would read as a
// student with no credits.
export default function StudentSidebar({
  student, name, credits, packageSummary,
  reads = { makeup: true, packages: true },
  // The running-out figures (#181), from `useStudentPackageForecast`. Optional:
  // a caller that has not wired it gets the balance.
  packageForecast = null,
  onViewFamily, onAddPackage, onPackageActivity,
  // `Set Make-Up Credits` (#178). Optional: without it the balance shows with
  // no link rather than a button that does nothing.
  onSetCredits = null,
  // The three pencils MMS puts on this rail (#120). Optional, so a read-only
  // rail still renders.
  onEditProfile = null, onEditLesson = null, onEditNote = null,
}) {
  const formatMoney = useMoneyFormat();
  const { decimalSeparator: decimal } = useLocale();
  const mode = coreSync().resolvePricingMode(student.pricingMode);
  const price = student.pricingMode === 'none'
    ? 'No charges'
    : `${formatMoney(student.rateMinor || 0)} · ${mode.label}`;

  return (
    <div>
      <Card>
        {/* MMS's profile pencil sits beside the avatar, top-left. Ours is
            top-right of the same card: this Card draws no header row for it to
            share, and the right edge is where every other action in this app
            lives. */}
        {onEditProfile ? (
          <div className={s.cardActions}>
            <EditPencil label="Edit Student" onClick={onEditProfile} />
          </div>
        ) : null}
        <div className={s.avatar}>{initials ? initials(name) : name.slice(0, 2)}</div>
        <div className={s.name}>{name}</div>
        <div className={s.centered}>
          <StatusBadge status={student.status} />
        </div>
        {student.familyId ? (
          <div className={s.familyLink}>
            <button className={s.link} onClick={onViewFamily}>View Family Account</button>
          </div>
        ) : null}
      </Card>

      <Card
        title="Lesson Settings"
        action={onEditLesson ? <EditPencil label="Edit Lesson Settings" onClick={onEditLesson} /> : null}
      >
        <DetailRow label="Category" value={student.categoryLabel || 'Lesson'} />
        {/* `–` rather than `null minutes` for a document without the field
            (#352; `studentDurationMin` in src/domain/people.js). */}
        <DetailRow
          label="Duration"
          value={student.durationMin == null ? '–' : `${student.durationMin} minutes`}
        />
        <DetailRow label="Lesson Price" value={price} />

        <div className={s.block}>
          <div className={s.blockLabel}>Make-Up Credits</div>
          <div className={s.blockValue}>
            {/* `formatCredits` (#352): a credit is minutes over the student's
                lesson length, and a student with no length has no quotient to
                show. In the studio's Region, `1,5` (#549). */}
            {reads.makeup ? (
              <>
                Booked: {formatCredits(credits.booked, decimal)} | Available: {formatCredits(credits.available, decimal)}
                {' | '}<b>Total: {formatCredits(credits.total, decimal)}</b>
              </>
            ) : figureOrDash(false)}
          </div>
          {/* MMS's `Set Make-Up Credits` is a row inside the Lesson Settings
              drawer this card's pencil opens. Ours is a link under the balance,
              because it writes a different collection on its own transaction
              (see the head of SetMakeupCreditsDrawer.jsx). */}
          {onSetCredits ? (
            <button className={s.noteToggle} onClick={onSetCredits}>
              Set Make-Up Credits
            </button>
          ) : null}
        </div>

        <div className={s.block}>
          <div className={s.blockLabel}>Package Time</div>
          <div className={s.blockValue}>
            {reads.packages
              ? <PackageTime summary={packageSummary} forecast={packageForecast} />
              : figureOrDash(false)}
          </div>
        </div>

        <div className={s.buttons}>
          <Button variant="secondary" onClick={onAddPackage}>Add Package</Button>
          <Button variant="secondary" onClick={onPackageActivity}>Package Activity</Button>
        </div>
      </Card>

      <Card
        title="Notes"
        action={onEditNote ? <EditPencil label="Edit Note" onClick={onEditNote} /> : null}
      >
        <NoteBody note={student.note} />
      </Card>
    </div>
  );
}
